/**
 * An inspection limitation row's warning colour — legacy's Aircraft List row
 * class (mod_aircraft_templates/includes/tabs/aircraft_templates_tab_aircraft_list.inc:99,
 * `<tr class="&var.classname; &var.warning_type;">`), which css/editTab.css:86-110
 * turns into TEXT colour:
 *   red   = overdue
 *   blue  = inside its warning window
 *   amber = flying on an extension: past the original due, not yet past next +
 *           extension (the logo amber) -- Aaron 2026-10-07, replacing
 *           legacy's green ("an extension is held"). The extension's value is the
 *           Remaining cell's ExtensionChip. A stored legacy "green" reads as no
 *           colour until the one-off recolour rewrites it.
 *
 * The value is the STORED colour the backend merges exactly as legacy's
 * aircraft_template_inspection_log_request does (red > amber > blue across the
 * airframe's sibling rows + linked_warning_color) — see
 * app/inspections/record_tabs.py:aircraft_list. Nothing is recomputed here.
 *
 * Same rule, labels and theme tokens as the aircraft list's
 * src/aircraft/aircraftListWarning.ts (Aaron 2026-09-27) so the two surfaces
 * read alike. Never bold, legacy's red bold included (Aaron 2026-10-07: "need to
 * remove all the bold"; "black, red, blue amber no bold").
 */
import { isConditionBased, isOverdue } from "./limitationRemaining";
import type { AircraftInspectionLimitation } from "./types";
import { ON_EXTENSION_COLOUR, ON_EXTENSION_LABEL } from "../components/ExtensionChip";

export type LimitationWarningColour = "red" | "amber" | "blue";

const ROW_CLASS: Record<LimitationWarningColour, string> = {
  red: "limitation-row--overdue",
  amber: "limitation-row--on-extension",
  blue: "limitation-row--due-soon",
};

const ROW_LABEL: Record<LimitationWarningColour, string> = {
  red: "Overdue",
  amber: ON_EXTENSION_LABEL,
  blue: "Coming due",
};

// Theme tokens, not legacy's red / blue / green literals (PATTERNS.md: a
// module view has no colour literals).
const ROW_COLOUR: Record<LimitationWarningColour, string> = {
  red: "error.main",
  amber: ON_EXTENSION_COLOUR,
  blue: "info.main",
};

export function limitationWarningColour(value: unknown): LimitationWarningColour | null {
  const colour = typeof value === "string" ? value.trim().toLowerCase() : "";
  return colour in ROW_CLASS ? (colour as LimitationWarningColour) : null;
}

export function limitationWarningRowClassName(row?: { warning_color?: string | null }): string {
  const colour = limitationWarningColour(row?.warning_color);
  return colour ? ROW_CLASS[colour] : "";
}

export function limitationWarningLabel(row?: { warning_color?: string | null }): string {
  const colour = limitationWarningColour(row?.warning_color);
  return colour ? ROW_LABEL[colour] : "";
}

/** Spread into a grid's `sx`: colours the row's text, never bold (Aaron 2026-10-07). */
export const limitationWarningRowSx = Object.fromEntries(
  (Object.keys(ROW_CLASS) as LimitationWarningColour[]).map((colour) => {
    const cls = ROW_CLASS[colour];
    return [
      `& .${cls}, & .${cls} .MuiDataGrid-cell`,
      { color: ROW_COLOUR[colour] },
    ];
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// The aircraft Inspections tab's ROW COLOUR, for one limitation or a whole
// inspection row (its nested limitations). Aaron 2026-10-06: "we dont need
// status, its covered by the colour coding" -- the Status column is gone and the
// colour is the signal.
//
// It invents no threshold. Overdue is `isOverdue` (limitationRemaining.ts).
// Coming due (blue) and On extension (amber) come only from the colour as
// the server computes it (`live_warning_color`, app/aircraft/aircraft_warning.py
// due_reading -- "inside the row's OWN warning window"); without it the row is
// overdue-only. A condition-based row is never coloured: Aaron 2026-10-04, "On
// Condition is just another based on but with no maths -- keep it looking like
// the rest" -- the engine never measures it (aircraft.inc:348).

export type InspectionStatus = LimitationWarningColour | "";

/** Roll-up order (aircraft_evaluate_color, amber where legacy ranked green): red > amber > blue. */
const STATUS_RANK: Record<InspectionStatus, number> = { red: 3, amber: 2, blue: 1, "": 0 };

type StatusRow = Pick<AircraftInspectionLimitation, "based_on" | "days_remaining" | "units_remaining"> & {
  live_days_remaining?: number | null;
  live_units_remaining?: string | number | null;
  live_warning_color?: string | null;
};

/** One limitation's colour. */
export function limitationStatus(row: StatusRow): InspectionStatus {
  if (isConditionBased(row.based_on)) return "";
  if (isOverdue(row)) return "red";
  return limitationWarningColour(row.live_warning_color) ?? "";
}

/** An inspection row's colour: the most urgent of its limitations ("whichever first"). */
export function inspectionStatus(row: StatusRow & { group_limitations?: StatusRow[] }): InspectionStatus {
  const limitations = row.group_limitations?.length ? row.group_limitations : [row];
  let best: InspectionStatus = limitationStatus(limitations[0]);
  for (const lim of limitations.slice(1)) {
    const s = limitationStatus(lim);
    if (STATUS_RANK[s] > STATUS_RANK[best]) best = s;
  }
  return best;
}

/** The theme palette key for a colour, or undefined for none. */
export function inspectionStatusPalette(status: InspectionStatus): "error" | "info" | undefined {
  if (status === "red") return "error";
  if (status === "blue") return "info";
  return undefined;
}

/** The text colour for a status: a theme palette main, or the logo-amber token for amber. */
export function inspectionStatusTextColour(status: InspectionStatus, theme: any): string | undefined {
  if (status === "amber") return ON_EXTENSION_COLOUR;
  const key = inspectionStatusPalette(status);
  return key ? theme.palette[key].main : undefined;
}

/**
 * An `sx` colour for a row's text: its status colour, else `fallback` (black,
 * text.primary). Aaron 2026-10-07: the aircraft grids' text is only black, red,
 * blue or amber -- no accent colour, no bold.
 */
export function inspectionStatusSxColour(status: InspectionStatus, fallback = "text.primary") {
  return (theme: any): string => {
    const colour = inspectionStatusTextColour(status, theme);
    if (colour) return colour;
    const [key, shade] = fallback.split(".");
    return theme.palette[key]?.[shade] ?? fallback;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The row classes and the whole-row colour rule, shared by the aircraft
// Inspections and Modifications tabs (lifted from AircraftInspectionLimitations.tsx,
// Aaron 2026-10-11: "ensure all updates to inspections also apply to modifications").
// `--overdue` is the class the Inspections tab has always set (its tests pin it);
// `--due-soon` / `--on-extension` follow ROW_CLASS's names. Amber replaced legacy's
// extension green (Aaron 2026-10-07); the extension's value is the Remaining cell's chip.
const ROW_SUFFIX: Record<LimitationWarningColour, string> = {
  red: "overdue",
  amber: "on-extension",
  blue: "due-soon",
};

/** The class for a status's colour, `${prefix}--overdue` etc.; "" for none. */
export function inspectionRowClassName(status: InspectionStatus, prefix = "inspection-row"): string {
  return status === "red" || status === "amber" || status === "blue" ? `${prefix}--${ROW_SUFFIX[status]}` : "";
}

/**
 * Spread into a grid's `sx`: the whole row's text in its status's theme token, out-ranking
 * a title link's own colour -- the Components tab's componentRowColourRule
 * (src/aircraft/AircraftComponent.tsx). Never bold (Aaron 2026-10-07: "need to remove all
 * the bold"). A several-limit row's OTHER lines (`limit-line--other`, StackedLines'
 * STACKED_LINE_OTHER_CLASS) keep their grey, so the line in the row's colour is the one
 * that comes due first. A StackedLine `tone` ("other" grey, "overdue" red) holds against
 * the row's colour for everything inside the line too (a link, a Typography): the rules
 * after the row's, at its specificity or above, so they win.
 */
export function inspectionRowColourSx(prefix: string) {
  return Object.fromEntries(
    (["red", "amber", "blue"] as const).map((status) => [
      `& .${prefix}--${ROW_SUFFIX[status]}`,
      {
        "&, & .MuiDataGrid-cell, & a, & .MuiTypography-root:not(.limit-line--other), & span": {
          color: (theme: any) => `${inspectionStatusTextColour(status, theme)} !important`,
        },
        // The doubled selector out-ranks the row's `.MuiTypography-root:not(…)` rule (two
        // classes there), so a Typography inside a toned line keeps the line's tone.
        "& .limit-line--other, & .limit-line--other.limit-line--other *": {
          color: (theme: any) => `${theme.palette.text.secondary} !important`,
        },
        "& [data-line-tone='overdue'], & [data-line-tone='overdue'][data-line-tone] *": {
          color: (theme: any) => `${theme.palette.error.main} !important`,
        },
      },
    ])
  );
}
